/**
 * optionLabel — derive "Option N" labels from line-item order.
 *
 * PR-62 · §347 §4 B11 · N-2 (§3au numbering) · Portica rulings 361_ + 362_
 * (2026-08-19).
 *
 * The old model stored an `optionName` on every line in a group and rendered
 * it directly. That model failed three ways:
 *   1. Reordering groups did not renumber ("Option 2" stayed "Option 2" even
 *      after moving to the top).
 *   2. Deleting a group left gaps ("Option 1" and "Option 3", no "Option 2").
 *   3. Multiple lines in one group had to be updated atomically to keep the
 *      stored label consistent.
 *
 * §3au: number-only, derived at display time. `optionName` / `optionDescription`
 * are removed from the write path (361_ §1 Q1). The stored data on each option
 * line is just `optionId` — a stable UUID identifying which group it belongs
 * to. The label ("Option 1", "Option 2", ...) is computed at render time from
 * the first-occurrence position of each distinct optionId in the line-item
 * order.
 *
 * Consequences that fall out for free:
 *   • Reorder a group → the derived number moves with it.
 *   • Delete a group → the remaining groups renumber contiguously.
 *   • Sent quote (§3aw) → lineItems is frozen → derivation is stable.
 *   • Migrated quotes with no optionId → collectOptionGroupIds returns [] and
 *     the caller renders nothing option-group-flavored (per 361_ §1 Q3
 *     measurement: 0/25 tenant-270 quotes carry any option group).
 *
 * Consumers (PR-62 wiring targets):
 *   • components/quotes/builder/AreaCard.js (OptionsAreaCard group header)
 *   • components/quotes/builder/QuoteBuilderPage.js (group label in
 *     commitGroups / area rendering)
 *
 * DELIBERATELY NOT a consumer in PR-62 (per 362_ §1):
 *   • components/shared/QuoteSelectionPanel.js — the customer-facing grouped
 *     presentation is deferred to PR-63/B12, because introducing group-keyed
 *     option selection there would collide with §3k (alternates are
 *     group-keyed today; options are line-keyed) without B12's measurement.
 *
 * VOCAB (per 363_ §1 + N-14):
 *   • `optionId` (camelCase) is CANONICAL. It is the spelling every writer
 *     in this codebase emits (AddItemCascade:320, AreaCard:302). Nothing
 *     writes `option_id`. If snake_case ever appears on the wire (e.g. from
 *     a future backend response), it is `ensureAliasesAgree.js`'s job to
 *     reconcile it before it reaches this helper — not this helper's.
 *   • This module reads `optionId` only. A fallback to `option_id` was
 *     considered and rejected: an unreachable branch would hide drift
 *     rather than resolve it (§4.1).
 *
 * @module quote-builder/optionLabel
 */

import { isOptionLine } from './separateOptions';

/**
 * Extract distinct optionIds from lineItems in first-occurrence order,
 * restricted to lines classified as option lines.
 *
 * Non-option lines are ignored (they cannot participate in an option group).
 * A line with no `optionId` is ignored too — it is an ungrouped option line
 * (legacy migration shape, §3k "Ungrouped" bucket) and does not contribute
 * to the numbered set.
 *
 * @param {Array} lineItems  quote line items in canonical order
 * @returns {string[]}       distinct optionIds, ordered by first appearance
 */
export function collectOptionGroupIds(lineItems) {
  const seen = new Set();
  const ordered = [];
  const items = Array.isArray(lineItems) ? lineItems : [];
  for (const li of items) {
    if (!isOptionLine(li)) continue;
    const oid = li && li.optionId;
    if (!oid) continue;
    if (seen.has(oid)) continue;
    seen.add(oid);
    ordered.push(oid);
  }
  return ordered;
}

/**
 * Derive the display label for an option group by its position in the
 * ordered set of group ids on the quote.
 *
 * @param {string[]} optionGroupIds  from collectOptionGroupIds(lineItems)
 * @param {string}   optionId        the group id to label
 * @returns {string}                 "Option N", or '' if optionId not found
 */
export function deriveOptionLabel(optionGroupIds, optionId) {
  if (!Array.isArray(optionGroupIds) || !optionId) return '';
  const idx = optionGroupIds.indexOf(optionId);
  return idx >= 0 ? `Option ${idx + 1}` : '';
}
